"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.clearManagerAssistantChat = exports.saveManagerAssistantChat = exports.loadManagerAssistantChat = exports.getManagerAssistantChatStorageKey = void 0;
const util_1 = require("@app/util");
const STORAGE_VERSION = 2;
const STORAGE_PREFIX = 'ds-manager-assistant-chat';
const isRecord = (value) => typeof value === 'object' && (0, util_1.isNotNull)(value);
const getManagerAssistantChatStorageKey = (userId, workspaceId) => `${STORAGE_PREFIX}:${userId}:${workspaceId}`;
exports.getManagerAssistantChatStorageKey = getManagerAssistantChatStorageKey;
const restoreDate = (value) => {
    if (typeof value !== 'string')
        return undefined;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};
const restoreMessage = (value) => {
    if (!isRecord(value))
        return null;
    const { role, content } = value;
    if (role !== 'assistant' && role !== 'user' && role !== 'system')
        return null;
    if (typeof content !== 'string' && !Array.isArray(content))
        return null;
    return {
        ...(typeof value.id === 'string' ? { id: value.id } : {}),
        role,
        content: content,
        ...((0, util_1.isValue)(restoreDate(value.createdAt))
            ? { createdAt: restoreDate(value.createdAt) }
            : {}),
        ...(Array.isArray(value.attachments)
            ? { attachments: value.attachments }
            : {}),
    };
};
const sanitizeAttachment = (attachment) => {
    if (!isRecord(attachment))
        return null;
    if (typeof attachment.id !== 'string' || typeof attachment.name !== 'string')
        return null;
    const type = typeof attachment.type === 'string' ? attachment.type : 'document';
    const contentType = typeof attachment.contentType === 'string'
        ? attachment.contentType
        : undefined;
    return {
        id: attachment.id,
        type,
        name: attachment.name,
        ...((0, util_1.isValue)(contentType) ? { contentType } : {}),
        status: { type: 'complete' },
        // Never persist File objects or base64 previews. On restore, attachments are
        // filename-only chips; n8n keeps the actual uploaded files in the session folder.
        content: [],
    };
};
const sanitizeMessage = (message) => {
    if (message.metadata?.isOptimistic === true)
        return null;
    const content = typeof message.content === 'string'
        ? message.content
        : message.content.filter((part) => part.type !== 'image' &&
            part.type !== 'file' &&
            part.type !== 'audio');
    const attachments = (message.attachments ?? [])
        .map(sanitizeAttachment)
        .filter(util_1.isValue);
    return {
        ...((0, util_1.isValue)(message.id) ? { id: message.id } : {}),
        role: message.role,
        content,
        ...((0, util_1.isValue)(message.createdAt) ? { createdAt: message.createdAt } : {}),
        ...(attachments.length > 0 ? { attachments } : {}),
    };
};
const loadManagerAssistantChat = ({ userId, workspaceId, staleMs, }) => {
    const key = (0, exports.getManagerAssistantChatStorageKey)(userId, workspaceId);
    const raw = window.sessionStorage.getItem(key);
    if (!(0, util_1.isValue)(raw))
        return null;
    try {
        const parsed = JSON.parse(raw);
        if (!isRecord(parsed))
            return null;
        if (parsed.version !== STORAGE_VERSION ||
            parsed.userId !== userId ||
            parsed.workspaceId !== workspaceId ||
            typeof parsed.sessionId !== 'string' ||
            typeof parsed.lastExchangeAt !== 'number' ||
            Date.now() - parsed.lastExchangeAt > staleMs ||
            !Array.isArray(parsed.messages)) {
            window.sessionStorage.removeItem(key);
            return null;
        }
        const messages = parsed.messages.map(restoreMessage).filter(util_1.isValue);
        return {
            sessionId: parsed.sessionId,
            lastExchangeAt: parsed.lastExchangeAt,
            ...(typeof parsed.onboardingStep === 'string'
                ? { onboardingStep: parsed.onboardingStep }
                : {}),
            messages,
        };
    }
    catch {
        window.sessionStorage.removeItem(key);
        return null;
    }
};
exports.loadManagerAssistantChat = loadManagerAssistantChat;
const saveManagerAssistantChat = ({ sessionId, userId, workspaceId, lastExchangeAt, onboardingStep, messages, }) => {
    const sanitizedMessages = messages.map(sanitizeMessage).filter(util_1.isValue);
    if (sanitizedMessages.length === 0)
        return;
    const payload = {
        version: STORAGE_VERSION,
        sessionId,
        userId,
        workspaceId,
        lastExchangeAt,
        ...((0, util_1.isValue)(onboardingStep) ? { onboardingStep } : {}),
        messages: sanitizedMessages,
    };
    window.sessionStorage.setItem((0, exports.getManagerAssistantChatStorageKey)(userId, workspaceId), JSON.stringify(payload));
};
exports.saveManagerAssistantChat = saveManagerAssistantChat;
const clearManagerAssistantChat = (userId, workspaceId) => {
    window.sessionStorage.removeItem((0, exports.getManagerAssistantChatStorageKey)(userId, workspaceId));
};
exports.clearManagerAssistantChat = clearManagerAssistantChat;
